/**
 * MandantWizardWrapper — re-uses die existierenden Step-Komponenten
 * (TriageStep, IdentityStep, …) für den Mandanten-Pfad.
 *
 * Architektur-Entscheidung (siehe CLAUDE.md):
 * Wir mappen die URL `/mandant/case/:id/wizard/:step` auf dieselbe
 * `:id`-Param-Struktur wie der Berater-Pfad, damit useParams<{id}> in den
 * existierenden Steps unverändert funktioniert. Die Audience-Information
 * fließt über den <WizardAudienceProvider> in die Komponenten — Steps
 * müssen nichts wissen, aber WizardLayout/WizardStep richten Navigation
 * und Footer-Texte auf den Mandanten-Pfad aus.
 *
 * Schutz vor Cockpit-Übergängen: Steps verlinken nicht ins Cockpit, weil
 * die Cockpit-Verlinkung im Hub liegt — der Mandant sieht den Hub nie.
 *
 * Polish (Phase A): Sticky-Topbar mit SaveStatusIndicator. Der Wrapper
 * startet `useWizardSync`, damit der Mandant nicht nur lokal speichert,
 * sondern auch zur DB synchronisiert wird (Berater muss seine Eingaben
 * sehen können).
 */

import { type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { WizardAudienceProvider } from '../components/wizard/WizardAudienceContext';
import {
  SaveStatusIndicator,
  type SaveStatusKind,
} from '../components/mandant/SaveStatusIndicator';
import { useWizardSync } from '../lib/store/useWizardSync';
import { useAuthOrSession } from '../lib/authOrSession';
import { useWizardStore } from '../lib/store/wizard';

export function MandantWizardWrapper({ children }: { children: ReactNode }) {
  return (
    <WizardAudienceProvider audience="mandant">
      <MandantWizardScaffold>{children}</MandantWizardScaffold>
    </WizardAudienceProvider>
  );
}

/**
 * Innere Hülle, die den useWizardSync-Hook ausführt und den Save-Status
 * dezent oben rechts darstellt. Wir trennen den Wrapper, damit der
 * Audience-Provider auch im Strict-Mode keine doppelten Renders verursacht.
 */
function MandantWizardScaffold({ children }: { children: ReactNode }) {
  const { id: caseId = '' } = useParams<{ id?: string }>();
  const auth = useAuthOrSession();

  // Sync läuft nur mit echtem User. Im Mock bleibt status='idle'.
  const sync = useWizardSync(caseId, {
    userId: auth.isMock ? null : (auth.user?.id ?? null),
    // Mandant-Erfassung schreibt in die Mandant-Ebene.
    layer: 'mandant',
  });

  const lastSavedAt = useWizardStore((s) => s.cases[caseId]?.lastSavedAt) ?? null;

  // Mapping useWizardSync.status → SaveStatusKind. Die Zustandsmenge ist
  // nicht 1:1 deckungsgleich (loading/ready vs. saved), aber semantisch:
  //   loading      → 'saving' (initial DB-Roundtrip)
  //   saving       → 'saving'
  //   ready        → 'saved'  (sofern lastSavedAt existiert), sonst 'idle'
  //   error        → 'error'
  //   idle         → 'idle'
  const indicatorKind: SaveStatusKind =
    sync.status === 'error'
      ? 'error'
      : sync.status === 'saving' || sync.status === 'loading'
        ? 'saving'
        : lastSavedAt
          ? 'saved'
          : 'idle';

  return (
    <div className="relative">
      {/* Sticky-Topbar für den Save-Status. Sitzt zwischen AppFrame-Header
          und Wizard-Body. Auf Mobile bündig rechts, Desktop ebenfalls. */}
      <div className="sticky top-[var(--header-h)] z-[5] -mx-1 flex justify-end px-1 pt-3 md:pt-4">
        <SaveStatusIndicator
          status={indicatorKind}
          lastSavedAtIso={lastSavedAt}
          onRetry={() => {
            void sync.flush();
          }}
        />
      </div>
      {children}
    </div>
  );
}
